import type { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import type { DenyReason } from './claimCampaignTypes';

// ============================================
// Contract-based types (from definitions)
// ============================================
export type CustomRule = definitions['presentedCustomRule'];
export type CustomRuleInput = definitions['customRule'];
export type CustomRuleType = definitions['customRule']['type'];
export type CustomRulePurpose = definitions['customRule']['purpose'];
export type PointsType = definitions['customRule']['pointsType'];
export type ProductType = definitions['customRule']['productType'];
export type PageInfo = definitions['pageBasedPaginationStats'];
export type CustomRuleStatusCounts = definitions['customRuleStatusCounts'];
export type LoyaltyTier = definitions['accountSummary']['tier'];
export type PendingPointsStatus = definitions['pendingPoints']['status'];
export type AccountRegion = definitions['accountRegions'];

// ============================================
// matchConfig (custom rule flexible matching)
// ============================================
// Types are derived from the contract so they stay in sync; the runtime
// constants/helpers below have no contract equivalent and stay local.

/** Declarative deeper-filter config layered on top of the exact identifier anchor. */
export type MatchConfig = NonNullable<CustomRuleInput['matchConfig']>;

/** A single match condition: apply `op` between the item's `field` value and `value`. */
export type MatchCondition = MatchConfig['match'][number];

/** Identifier fields a single match condition can target. */
export type MatchField = MatchCondition['field'];

/** Supported matching operators for a custom rule deeper filter. */
export type MatchOp = MatchCondition['op'];

export const MATCH_FIELD_OPTIONS: ReadonlyArray<MatchField> = [
  'earnKey',
  'offerId',
  'packageId',
  'promoKey',
  'productType',
] as const;

export const MATCH_OP_OPTIONS: ReadonlyArray<MatchOp> = [
  'exact',
  'startsWith',
  'endsWith',
  'contains',
  'regex',
] as const;

export const MATCH_COMBINE_OPTIONS = ['AND', 'OR'] as const;

/** Max length for ibsEarnKey (mirrors server Zod schema). */
export const IBS_EARN_KEY_MAX_LENGTH = 100;
/** Max length for a non-regex match value. */
export const MATCH_VALUE_MAX_LENGTH = 100;
/** Max length for a regex match value (mirrors server REGEX_VALUE_MAX_LENGTH). */
export const REGEX_VALUE_MAX_LENGTH = 200;

/** A fresh, empty match condition for seeding new rows. */
export const DEFAULT_MATCH_CONDITION: MatchCondition = {
  field: 'earnKey',
  op: 'startsWith',
  value: '',
};

/**
 * Builds a fresh default matchConfig used when the user first enables the section.
 * @returns A new MatchConfig with one empty condition.
 */
export function createDefaultMatchConfig(): MatchConfig {
  return { combine: 'AND', ibsEarnKey: '', match: [{ ...DEFAULT_MATCH_CONDITION }] };
}

// ============================================
// Custom Rules API types
// ============================================
export type CustomRuleSearchParams =
  paths['/api/lux-loyalty/custom-rules/search']['post']['requestBody']['content']['application/json'] & {
    status?: 'all' | 'active' | 'scheduled' | 'expired';
  };

export type CustomRuleSearchResponse =
  paths['/api/lux-loyalty/custom-rules/search']['post']['responses']['200']['content']['application/json'] & {
    result: {
      customRules: Array<definitions['presentedCustomRule']>;
      pageInfo: PageInfo;
      counts: CustomRuleStatusCounts;
    };
  };

export type CreateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules']['post']['requestBody']['content']['application/json'];
export type CreateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules']['post']['responses']['201']['content']['application/json'];

export type UpdateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['requestBody']['content']['application/json'];
export type UpdateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['responses']['200']['content']['application/json'];

// ============================================
// Manual Accruals API types (from contract)
// ============================================
export type ManualAccrualStatus = 'PENDING' | 'PROCESSED' | 'FAILED';

export type ManualAccrualsResponse =
  paths['/api/lux-loyalty/manual-accruals']['get']['responses']['200']['content']['application/json'];

export type ManualAccrualFilterParams = paths['/api/lux-loyalty/manual-accruals']['get']['parameters']['query'];

export type ManualAccrualsRetryRequestBody =
  paths['/api/lux-loyalty/manual-accruals']['put']['requestBody']['content']['application/json'];
export type ManualAccrualsRetryResponse =
  paths['/api/lux-loyalty/manual-accruals']['put']['responses']['200']['content']['application/json'];

// Extract ManualAccrual type from the response
export type ManualAccrual = ManualAccrualsResponse['result'][number];

// Extract nested types from ManualAccrual
export type PendingPoints = NonNullable<ManualAccrual['pendingPoints']>;
export type CustomRuleApplied = PendingPoints['customRulesApplied'][number];

// ============================================
// Accrual Log API types (from contract)
// ============================================

/** Raw response from GET /api/lux-loyalty/manual-accruals/accrual-log */
type RawAccrualLogResponse =
  paths['/api/lux-loyalty/manual-accruals/accrual-log']['get']['responses']['200']['content']['application/json'];

/** Raw result object from the contract (before extending manualAccrual) */
type RawAccrualLogResult = RawAccrualLogResponse['result'];

/** A single IBS accrual attempt for an order item */
export type IbsAccrualLog = RawAccrualLogResult['ibsAccrualLogs'][number];

/** A manual accrual record as returned in the accrual log */
export type AccrualLogManualAccrual = NonNullable<RawAccrualLogResult['manualAccrual']>;

/** A points adjustment record (manual adjustments applied to a customer account) */
export type PointsAdjustmentRecord = RawAccrualLogResult['pointsAdjustmentRecords'][number];

/** Combined accrual activity log for an order item */
export type AccrualLogResult = RawAccrualLogResult;

/** Full response from GET /api/lux-loyalty/manual-accruals/accrual-log */
export type AccrualLogResponse = RawAccrualLogResponse;

// ============================================
// Manual Accrual Status Update types (from contract)
// ============================================

/** Request body for updating a manual accrual status */
export type UpdateManualAccrualStatusRequest =
  paths['/api/lux-loyalty/manual-accruals/{itemId}/status']['patch']['requestBody']['content']['application/json'];

/** Response from updating a manual accrual status */
export type UpdateManualAccrualStatusResponse =
  paths['/api/lux-loyalty/manual-accruals/{itemId}/status']['patch']['responses']['200']['content']['application/json'];

// ============================================
// Calculate Earn API types
// ============================================
export type CalculateEarnRequest =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['requestBody']['content']['application/json'];

export type CalculateEarnResponse =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['responses']['200']['content']['application/json'];

// The valid loyaltyProductType values for the calculate-earn endpoint (narrower than ProductType)
export type CalculateEarnProductType = CalculateEarnRequest['productDetails']['loyaltyProductType'];

// Extract nested types from CalculateEarnResponse
export type PointDetail = CalculateEarnResponse['result']['pointDetails'][number];
export type CustomRuleBreakdown = CalculateEarnResponse['result']['customRulesBreakdown'][number];

// ============================================
// Loyalty Account types (from contract)
// ============================================
/** Contract account summary extended until contract-svc-lux-loyalty includes infinite tier. */
export type LoyaltyAccountSummary = Omit<definitions['accountSummary'], 'tier'> & {
  tier: definitions['accountSummary']['tier'] | 'infinite';
  customerServiceRep?: App.LuxLoyaltyCustomerServiceRepSummary | null;
};

type ContractGetLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['get']['responses']['200']['content']['application/json'];

export type GetLoyaltyAccountResponse = Omit<ContractGetLoyaltyAccountResponse, 'result'> & {
  result: Omit<ContractGetLoyaltyAccountResponse['result'], 'accountSummary'> & {
    accountSummary: LoyaltyAccountSummary;
  };
};

export type CreateLoyaltyAccountRequest =
  paths['/api/lux-loyalty/account']['post']['requestBody']['content']['application/json'];

export type CreateLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['post']['responses']['201']['content']['application/json'];

// ============================================
// Promo Campaign types (from contract)
// ============================================
export type PromoCampaignPresenter = definitions['promoCampaignPresenter'];
export type PromoCampaignBody = definitions['promoCampaignBody'];
export type PromoCampaignUpdateBody = definitions['promoCampaignUpdateBody'];
export type PromoCampaignAssignmentStats = definitions['promoCampaignAssignmentStats'];

export type PromoCampaignBenefitCode = PromoCampaignPresenter['benefit'];
export type PromoCampaignRegion = PromoCampaignPresenter['region'];
export type PromoCampaignTier = PromoCampaignPresenter['tiers'][number];

export const PROMO_CAMPAIGN_TIERS: ReadonlyArray<PromoCampaignTier> = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
] as const;

export type PromoCampaignSearchParams =
  paths['/api/lux-loyalty/promo-campaigns/search']['post']['requestBody']['content']['application/json'];

export type PromoCampaignSearchResponse =
  paths['/api/lux-loyalty/promo-campaigns/search']['post']['responses']['200']['content']['application/json'];

export type PromoCampaignCreateResponse =
  paths['/api/lux-loyalty/promo-campaigns']['post']['responses']['201']['content']['application/json'];

export type PromoCampaignCreateRequestBody =
  paths['/api/lux-loyalty/promo-campaigns']['post']['requestBody']['content']['application/json'];

export type PromoCampaignReadResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['get']['responses']['200']['content']['application/json'];

export type PromoCampaignUpdateRequestBody =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['patch']['requestBody']['content']['application/json'];

export type PromoCampaignUpdateResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['patch']['responses']['200']['content']['application/json'];

export type PromoCampaignDeleteResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['delete']['responses']['200']['content']['application/json'];

// ============================================
// Constants (runtime arrays for UI dropdowns)
// ============================================
export const PROMO_CAMPAIGN_BENEFIT_CODES: ReadonlyArray<PromoCampaignBenefitCode> = [
  'airport_transfer',
  'discounted_insurance',
  'early_access',
  'hidden_access',
  'lounge_pass',
  'phone_support',
  'tour_exp_credit',
  'travelshoot_credit',
  'hotel_upgrade',
] as const;

export const PROMO_CAMPAIGN_REGIONS: ReadonlyArray<PromoCampaignRegion> = [
  'AU',
  'CA',
  'CN',
  'FR',
  'DE',
  'HK',
  'IN',
  'ID',
  'IE',
  'IL',
  'IT',
  'JP',
  'KR',
  'MO',
  'MY',
  'NL',
  'NZ',
  'PH',
  'QA',
  'SA',
  'SG',
  'ZA',
  'ES',
  'TW',
  'TH',
  'AE',
  'GB',
  'US',
  'VN',
] as const;

export const PRODUCT_TYPE_OPTIONS: ReadonlyArray<NonNullable<ProductType>> = [
  'hotel_flash',
  'hotel_lpc',
  'hotel_lpp_eps',
  'le_tour',
  'tour_bank',
  'trusted_partner_tour',
  'tour_optional_extra',
  'experience',
  'cruise_group_1',
  'cruise_group_2',
  'cruise_group_3',
  'dom_economy',
  'dom_business',
  'int_economy',
  'int_premium_economy',
  'int_business',
  'int_first',
  'lux_plus',
  'gift_card',
  'car_hire',
  'rental',
  'cfmr',
  'insurance',
  'shop_tier_1',
  'shop_tier_2',
  'shop_tier_3',
  'shop_tier_4',
  'shop_tier_5',
  'shop_shipping',
] as const;

export const CUSTOM_RULE_TYPE_OPTIONS: ReadonlyArray<CustomRuleType> = [
  'BONUS_POINTS',
  'BONUS_POINTS_ONLY',
  'MULTIPLE_ON_BASE_POINTS',
] as const;

export const CUSTOM_RULE_PURPOSE_OPTIONS: ReadonlyArray<CustomRulePurpose> = [
  'ESTIMATION_ONLY',
  'ESTIMATION_AND_EARN',
  'ACCRUAL_OVERRIDE',
] as const;

export const POINTS_TYPE_OPTIONS: ReadonlyArray<PointsType> = [
  'BASE_POINTS',
  'BONUS_POINTS',
  'STATUS_CREDITS',
  'BONUS_STATUS_CREDITS',
] as const;

export const MANUAL_ACCRUAL_STATUS_OPTIONS: ReadonlyArray<ManualAccrualStatus> = [
  'PENDING',
  'PROCESSED',
  'FAILED',
] as const;

// ============================================
// Points Adjustment API types (from contract)
// ============================================
export type PointsAdjustmentRequestBody =
  paths['/api/lux-loyalty/points/adjustments']['post']['requestBody']['content']['application/json'];

export type PointsAdjustmentResponse =
  paths['/api/lux-loyalty/points/adjustments']['post']['responses']['200']['content']['application/json'];

// ============================================
// Tier Change API types (from contract)
// ============================================
type ContractTierChangeRequestBody =
  paths['/api/lux-loyalty/account/tier-change']['post']['requestBody']['content']['application/json'];

/** Contract tier-change body extended until contract-svc-lux-loyalty includes infinite. */
export type TierChangeRequestBody = Omit<ContractTierChangeRequestBody, 'targetTier'> & {
  targetTier: ContractTierChangeRequestBody['targetTier'] | 'infinite';
};

export type TierChangeResponse =
  paths['/api/lux-loyalty/account/tier-change']['post']['responses']['200']['content']['application/json'];

// ============================================
// Claim Campaign types (from contract)
// ============================================
export type ClaimCampaign = definitions['claimCampaignPresenter'];
export type ClaimCampaignStatus = ClaimCampaign['status'];
export type ClaimCampaignType = ClaimCampaign['type'];
export type ClaimValidatorType = ClaimCampaign['validatorType'];
export type ListCampaignsResponse =
  paths['/api/lux-loyalty/claims/campaigns/all']['get']['responses']['200']['content']['application/json'];

export type AdminSubmitClaimRequestBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['requestBody']['content']['application/json'];

export type AdminSubmitClaimResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['responses']['200']['content']['application/json'];

// ============================================
// Approval Request types (from contract)
// ============================================
export type ListApprovalRequestsResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/approval-requests']['get']['responses']['200']['content']['application/json'];

type ContractApprovalRequest = ListApprovalRequestsResponse['result']['requests'][number];

/**
 * Approval request shape with fields added by svc-lux-loyalty (denyReason, attemptNumber).
 * Both fields are optional until the contract package is updated —
 * remove these extensions once contract-svc-lux-loyalty exposes them.
 */
export type ApprovalRequest = ContractApprovalRequest & {
  denyReason?: DenyReason | null;
  attemptNumber?: number;
};

export type ApprovalRequestStatus = ContractApprovalRequest['status'];

export type ListApprovalRequestsParams =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/approval-requests']['get']['parameters']['query'];

export type ApproveApprovalRequestBody =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/approve']['post']['requestBody']['content']['application/json'];

export type ApproveApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/approve']['post']['responses']['200']['content']['application/json'];

export type GetApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}']['get']['responses']['200']['content']['application/json'];

/**
 * Reject body — denyReason required, notes optional (required only when reason is OTHER, enforced in UI).
 * TODO: Revert to the contract-derived type once denyReason is added to contract-svc-lux-loyalty.
 */
export type RejectApprovalRequestBody = {
  denyReason: DenyReason;
  notes?: string;
};

export type RejectApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/reject']['post']['responses']['200']['content']['application/json'];

// ============================================
// Claims UI types
// ============================================
export interface ClaimCsvRow {
  customerId: string;
  code?: string;
}

export interface BulkClaimRowResult {
  customerId: string;
  success: boolean;
  claimRecordId?: string;
  error?: string;
}

export interface BulkClaimResult {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  results: Array<BulkClaimRowResult>;
}

// ============================================
// Admin eSIM generate (not yet in contract package)
// POST /api/lux-loyalty/admin/esim/generate
// ============================================
export interface AdminEsimGenerateRequestBody {
  orderId: string;
  customerId: string;
  /** ISO 8601 datetime string. Sourced from the order's first hotel/bedbank/tour/cruise check-in. */
  departureDate: string;
}

export interface AdminEsimGenerateResult {
  orderId: string;
  promocode: string;
  packageSize: string;
  tier: string;
  partner: string;
}

export interface AdminEsimGenerateResponse {
  status: number;
  message: string;
  result: AdminEsimGenerateResult;
}

// ============================================
// Points Purchase Offers + Campaigns API types (from contract)
// ============================================

export type ListPointsPurchaseOffersAdminResponse =
  paths['/api/lux-loyalty/admin/points/purchase-offers']['get']['responses']['200']['content']['application/json'];

export type PointsPurchaseOffer = ListPointsPurchaseOffersAdminResponse['result'][number];

export type ListPointsPurchaseOffersResponse =
  paths['/api/lux-loyalty/points/purchase-offers']['get']['responses']['200']['content']['application/json'];

export type PointsPurchaseOfferPreview = ListPointsPurchaseOffersResponse['result'][number];

export type CreatePointsPurchaseOfferRequestBody =
  paths['/api/lux-loyalty/points/purchase-offers']['post']['requestBody']['content']['application/json'];

export type CreatePointsPurchaseOfferResponse =
  paths['/api/lux-loyalty/points/purchase-offers']['post']['responses']['201']['content']['application/json'];

export type UpdatePointsPurchaseOfferRequestBody =
  paths['/api/lux-loyalty/points/purchase-offers/{offerId}']['patch']['requestBody']['content']['application/json'];

export type UpdatePointsPurchaseOfferResponse =
  paths['/api/lux-loyalty/points/purchase-offers/{offerId}']['patch']['responses']['200']['content']['application/json'];

export type ListPointsPurchaseOfferCampaignsResponse =
  paths['/api/lux-loyalty/points/purchase-offer-campaigns']['get']['responses']['200']['content']['application/json'];

export type PointsPurchaseOfferCampaign = ListPointsPurchaseOfferCampaignsResponse['result'][number];

export type CreatePointsPurchaseOfferCampaignRequestBody =
  paths['/api/lux-loyalty/points/purchase-offer-campaigns']['post']['requestBody']['content']['application/json'];

export type CreatePointsPurchaseOfferCampaignResponse =
  paths['/api/lux-loyalty/points/purchase-offer-campaigns']['post']['responses']['201']['content']['application/json'];

export type UpdatePointsPurchaseOfferCampaignRequestBody =
  paths['/api/lux-loyalty/points/purchase-offer-campaigns/{campaignId}']['patch']['requestBody']['content']['application/json'];

export type UpdatePointsPurchaseOfferCampaignResponse =
  paths['/api/lux-loyalty/points/purchase-offer-campaigns/{campaignId}']['patch']['responses']['200']['content']['application/json'];

export type UpdatePointsPurchaseRecordRequestBody =
  paths['/api/lux-loyalty/points/purchase-records/{purchaseRecordId}']['patch']['requestBody']['content']['application/json'];

export type UpdatePointsPurchaseRecordResponse =
  paths['/api/lux-loyalty/points/purchase-records/{purchaseRecordId}']['patch']['responses']['200']['content']['application/json'];

export type CancelPointsPurchaseRecordRequestBody = Extract<UpdatePointsPurchaseRecordRequestBody, { op: 'cancel' }>;

export type PointsPurchaseOfferDerivedStatus = PointsPurchaseOffer['status'];
export type PointsPurchaseOfferCampaignType = PointsPurchaseOfferCampaign['type'];

// Presented status from the list endpoint (includes derived values from dates)
export type PointsPurchaseOfferCampaignStatus = PointsPurchaseOfferCampaign['status'];

// Writable status accepted by the update endpoint (stored values only)
export type PointsPurchaseOfferCampaignStoredStatus = NonNullable<
  UpdatePointsPurchaseOfferCampaignRequestBody['status']
>;

export const POINTS_PURCHASE_OFFER_STATUSES: ReadonlyArray<PointsPurchaseOfferDerivedStatus> = [
  'ACTIVE',
  'INACTIVE',
  'SCHEDULED',
];

export type PointsPurchaseOfferWritableStatus = NonNullable<UpdatePointsPurchaseOfferRequestBody['status']>;

export const POINTS_PURCHASE_OFFER_WRITABLE_STATUSES: ReadonlyArray<PointsPurchaseOfferWritableStatus> = [
  'ACTIVE',
  'INACTIVE',
];

export const POINTS_PURCHASE_CAMPAIGN_STATUSES: ReadonlyArray<PointsPurchaseOfferCampaignStatus> = [
  'ACTIVE',
  'INACTIVE',
  'SCHEDULED',
  'EXPIRED',
];

export const POINTS_PURCHASE_CAMPAIGN_STORED_STATUSES: ReadonlyArray<PointsPurchaseOfferCampaignStoredStatus> = [
  'ACTIVE',
  'INACTIVE',
];

export const POINTS_PURCHASE_CAMPAIGN_TYPES: ReadonlyArray<PointsPurchaseOfferCampaignType> = [
  'PRICE_DISCOUNT',
  'BONUS_POINTS',
];

// Constants
// ============================================
// App Constants
// ============================================
export const MIN_START_DATE = '2025-04-01';
export const MAX_END_DATE = '2099-12-31';
export const DEFAULT_PAGE_SIZE = 10;
